/*
 * Product Upsell template
 * A general furniture product page that resolves the selected variant from its
 * option buttons, lets the customer toggle upsell add-ons, and adds the main
 * product plus any chosen add-ons to the basket in a single /cart/add.js call.
 * No external libraries.
 */
if (!customElements.get('product-upsell')) {
  class ProductUpsell extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-pu-data]');
      if (!dataEl) return;
      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[product-upsell] Could not parse configurator data:', e); // eslint-disable-line
        return;
      }
      if (!this.config.variants || !this.config.variants.length) return;
      this.config.addons = this.config.addons || [];

      this.cartDrawer = document.querySelector('cart-drawer');
      this.moneyFormat = this.config.moneyFormat || '£{{amount}}';

      const first = this.config.variants.find((v) => v.available) || this.config.variants[0];
      this.state = {
        options: first.options.slice(),
        qty: 1,
        addons: {}
      };

      this.cacheDom();
      this.bindEvents();
      this.render();
      this.placeDetails();
    }

    /* Move the details accordions under the media on desktop, back under the
       product info on mobile. Single DOM node, repositioned per breakpoint. */
    placeDetails() {
      if (!this.details || !this.detailsSlot || !this.detailsHome) return;
      const target = this.detailsMq.matches ? this.detailsSlot : this.detailsHome;
      if (this.details.parentElement !== target) target.appendChild(this.details);
    }

    cacheDom() {
      this.optionEls = Array.from(this.querySelectorAll('[data-pu-opt]'));
      this.galleryStage = this.querySelector('[data-pu-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-pu-thumb]'));
      this.expandBtn = this.querySelector('[data-pu-expand]');
      this.lightbox = this.querySelector('[data-pu-lightbox]');
      this.lightboxImg = this.querySelector('[data-pu-lightbox-img]');
      // Full-resolution sources for the lightbox: the product images (thumbs) or,
      // when there is only one image, the single stage image.
      const stageImg = this.galleryStage ? this.galleryStage.querySelector('img') : null;
      this.galleryImages = this.thumbs.length
        ? this.thumbs.map((t) => t.dataset.puFull).filter(Boolean)
        : (stageImg && stageImg.dataset.puFull ? [stageImg.dataset.puFull] : []);
      this.lightboxIndex = 0;
      this.priceEl = this.querySelector('[data-pu-price]');
      this.qtyVal = this.querySelector('[data-pu-qty-val]');
      this.qtyInc = this.querySelector('[data-pu-qty-inc]');
      this.qtyDec = this.querySelector('[data-pu-qty-dec]');
      this.addonEls = Array.from(this.querySelectorAll('[data-pu-addon]'));
      this.summaryList = this.querySelector('[data-pu-summary-list]');
      this.totalEl = this.querySelector('[data-pu-total]');
      this.payEl = this.querySelector('[data-pu-pay]');
      this.atc = this.querySelector('[data-pu-atc]');
      this.errorEl = this.querySelector('[data-pu-error]');
      this.summary = this.querySelector('[data-pu-summary]');

      // Details accordions: rendered under the product info (mobile home); moved
      // under the media on desktop. Remember its original parent for moving back.
      this.details = this.querySelector('[data-pu-details]');
      this.detailsSlot = this.querySelector('[data-pu-details-slot]');
      this.detailsHome = this.details ? this.details.parentElement : null;
      this.detailsMq = window.matchMedia('(min-width: 990px)');
    }

    bindEvents() {
      this.optionEls.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.state.options[Number(btn.dataset.puOptIndex)] = btn.dataset.puOptValue;
          this.render();
        });
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setHero(thumb.dataset.puThumb, thumb));
      });

      // Reposition the details accordions when crossing the desktop breakpoint.
      if (this.detailsMq) this.detailsMq.addEventListener('change', () => this.placeDetails());

      // Lightbox: open on stage image / expand button, navigate, close.
      if (this.lightbox && this.galleryImages.length) {
        const stageImg = this.galleryStage ? this.galleryStage.querySelector('img') : null;
        if (stageImg) {
          stageImg.style.cursor = 'zoom-in';
          stageImg.addEventListener('click', () => this.openLightbox());
        }
        if (this.expandBtn) this.expandBtn.addEventListener('click', () => this.openLightbox());

        this.lightbox.querySelectorAll('[data-pu-lightbox-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeLightbox());
        });
        const prev = this.lightbox.querySelector('[data-pu-lightbox-prev]');
        const next = this.lightbox.querySelector('[data-pu-lightbox-next]');
        if (prev) prev.addEventListener('click', () => this.stepLightbox(-1));
        if (next) next.addEventListener('click', () => this.stepLightbox(1));

        document.addEventListener('keydown', (e) => {
          if (this.lightbox.hidden) return;
          if (e.key === 'Escape') this.closeLightbox();
          else if (e.key === 'ArrowLeft') this.stepLightbox(-1);
          else if (e.key === 'ArrowRight') this.stepLightbox(1);
        });
      }

      if (this.qtyInc) this.qtyInc.addEventListener('click', () => this.changeQty(1));
      if (this.qtyDec) this.qtyDec.addEventListener('click', () => this.changeQty(-1));

      this.addonEls.forEach((card) => {
        const toggle = card.querySelector('[data-pu-toggle]');
        if (toggle) toggle.addEventListener('click', () => this.toggleAddon(card.dataset.puAddon, card));
      });

      if (this.atc) this.atc.addEventListener('click', () => this.addToCart());

      // Sync with the theme's native variant picker (if added as a block).
      document.addEventListener('on:variant:change', (e) => {
        if (!e.detail) return;
        this.themeVariantId = e.detail.variant ? e.detail.variant.id : 'none';
        this.render();
      });

      const sumToggle = this.querySelector('[data-pu-summary-toggle]');
      if (sumToggle && this.summary) {
        sumToggle.addEventListener('click', () => {
          const open = this.summary.classList.toggle('is-expanded');
          sumToggle.textContent = open ? 'Hide' : 'View';
        });
      }
    }

    /* Resolve the current variant. Prefer the theme variant picker's selection
       (when a Variant picker block is present); otherwise use the section's own
       option buttons. */
    get variant() {
      if (this.themeVariantId !== undefined) {
        if (this.themeVariantId === 'none') return undefined;
        return this.config.variants.find((v) => String(v.id) === String(this.themeVariantId));
      }
      return this.config.variants.find((v) =>
        v.options.every((opt, i) => opt === this.state.options[i])
      );
    }

    changeQty(delta) {
      this.state.qty = Math.max(1, this.state.qty + delta);
      this.render();
    }

    toggleAddon(key, card) {
      const addon = this.config.addons.find((a) => a.key === key);
      if (!addon || !addon.available) return;
      this.state.addons[key] = !this.state.addons[key];
      card.classList.toggle('is-selected', this.state.addons[key]);
      const toggle = card.querySelector('[data-pu-toggle]');
      if (toggle) toggle.textContent = this.state.addons[key] ? 'Added' : 'Add';
      if (this.state.addons[key]) {
        this.track('upsell_addon_added', { item: addon.title });
      }
      this.renderSummary();
    }

    setHero(src, thumb) {
      if (!this.galleryStage || !src) return;
      const img = this.galleryStage.querySelector('img');
      if (img) {
        img.src = src;
        // Keep the full-resolution source (used by the lightbox) in sync.
        if (thumb && thumb.dataset.puFull) img.dataset.puFull = thumb.dataset.puFull;
      }
      this.thumbs.forEach((t) => t.setAttribute('aria-selected', String(t === thumb)));
      const idx = this.thumbs.indexOf(thumb);
      if (idx > -1) this.lightboxIndex = idx;
    }

    /* ---------- Lightbox ---------- */
    openLightbox() {
      if (!this.lightbox || !this.galleryImages.length) return;
      const stageImg = this.galleryStage ? this.galleryStage.querySelector('img') : null;
      const current = stageImg ? stageImg.dataset.puFull : null;
      // Prefer the image currently on the stage (covers variant swaps whose image
      // isn't in the thumbnail list).
      const found = current ? this.galleryImages.indexOf(current) : -1;
      if (found > -1) {
        this.lightboxIndex = found;
        this.showLightboxImage(this.galleryImages[found]);
      } else {
        this.showLightboxImage(current || this.galleryImages[this.lightboxIndex] || this.galleryImages[0]);
      }
      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pu-lightbox-open');
      this.updateLightboxNav();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.hidden = true;
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pu-lightbox-open');
      if (this.expandBtn) this.expandBtn.focus();
    }

    stepLightbox(delta) {
      if (this.galleryImages.length < 2) return;
      const count = this.galleryImages.length;
      this.lightboxIndex = (this.lightboxIndex + delta + count) % count;
      this.showLightboxImage(this.galleryImages[this.lightboxIndex]);
      // Sync the on-page gallery so closing the lightbox leaves the matching hero.
      if (this.thumbs[this.lightboxIndex]) {
        this.setHero(this.thumbs[this.lightboxIndex].dataset.puThumb, this.thumbs[this.lightboxIndex]);
      }
      this.updateLightboxNav();
    }

    showLightboxImage(src) {
      if (this.lightboxImg && src) this.lightboxImg.src = src;
    }

    updateLightboxNav() {
      const multiple = this.galleryImages.length > 1;
      const prev = this.lightbox.querySelector('[data-pu-lightbox-prev]');
      const next = this.lightbox.querySelector('[data-pu-lightbox-next]');
      if (prev) prev.hidden = !multiple;
      if (next) next.hidden = !multiple;
    }

    render() {
      const variant = this.variant;

      // Option button states + availability.
      this.optionEls.forEach((btn) => {
        const idx = Number(btn.dataset.puOptIndex);
        const val = btn.dataset.puOptValue;
        btn.setAttribute('aria-pressed', String(this.state.options[idx] === val));
        // Mark a value unavailable if no available variant pairs it with the
        // rest of the current selection.
        const possible = this.config.variants.some((v) =>
          v.available && v.options[idx] === val &&
          v.options.every((o, i) => i === idx || o === this.state.options[i])
        );
        btn.classList.toggle('is-unavailable', !possible);
      });

      // Reflect the selected value in each option's label.
      this.querySelectorAll('[data-pu-opt-name]').forEach((el) => {
        el.textContent = this.state.options[Number(el.dataset.puOptName)];
      });

      // Swap hero to the variant image when it has one.
      if (variant && variant.image && this.galleryStage) {
        const img = this.galleryStage.querySelector('img');
        if (img) {
          img.src = variant.image;
          // Keep the full-resolution source (used by the lightbox) in sync.
          if (variant.imageFull) img.dataset.puFull = variant.imageFull;
        }
      }

      if (this.qtyVal) this.qtyVal.textContent = this.state.qty;

      // Price.
      if (this.priceEl) {
        if (variant) {
          const onSale = variant.compareAt && variant.compareAt > variant.price;
          let html = this.money(variant.price);
          if (onSale) {
            html += `<del>${this.money(variant.compareAt)}</del>`;
            const savePct = Math.round((1 - variant.price / variant.compareAt) * 100);
            if (savePct > 0) html += `<span class="pu-price__save">Save ${savePct}%</span>`;
          }
          this.priceEl.classList.toggle('pu-price--sale', Boolean(onSale));
          this.priceEl.innerHTML = html;

          // Black Friday "with code" price (snippets/bf-price.liquid).
          const bfEl = this.querySelector('[data-bf-price]');
          const bfKeep = bfEl ? Number(bfEl.dataset.bfKeep) : 0;
          const bfValue = bfEl ? bfEl.querySelector('[data-bf-price-value]') : null;
          if (bfValue && bfKeep) {
            bfValue.textContent = this.money(Math.round(variant.price * bfKeep / 100));
          }
        }
      }

      this.renderSummary();
    }

    renderSummary() {
      const variant = this.variant;
      const available = variant && variant.available;
      const rows = [];
      let total = 0;

      if (variant) {
        const line = variant.price * this.state.qty;
        total += line;
        rows.push({ label: `${this.state.qty}× ${this.config.productTitle}`, amount: line });
      }

      this.config.addons.forEach((addon) => {
        if (this.state.addons[addon.key] && addon.available) {
          total += addon.price;
          rows.push({ label: `1× ${addon.title}`, amount: addon.price });
        }
      });

      if (this.summaryList) {
        this.summaryList.innerHTML = rows.map((r) =>
          `<li class="pu-summary__row"><span>${r.label}</span><span>${this.money(r.amount)}</span></li>`
        ).join('');
      }
      if (this.totalEl) this.totalEl.textContent = this.money(total);

      if (this.payEl) {
        const four = (total / 4 / 100).toFixed(2);
        const clearpayLogo = this.payEl.dataset.clearpayLogo;
        const clearpayLabel = clearpayLogo
          ? `<img class="pu-pay__logo" src="${clearpayLogo}" alt="Clearpay">`
          : 'Clearpay:';
        this.payEl.innerHTML =
          `<span class="pu-pay__line">${clearpayLabel} 4 payments of £${four}</span>`;
      }

      this.total = total;

      // ATC state.
      if (this.atc) {
        this.atc.classList.toggle('is-soldout', !available);
        this.atc.disabled = !available;
        this.atc.textContent = available
          ? this.config.atcLabel
          : this.config.soldOutLabel;
      }
    }

    /* ---------- Cart ---------- */
    buildItems() {
      const variant = this.variant;
      const buildId = `up-${Math.floor(Date.now() / 1000)}`;
      const items = [{
        id: variant.id,
        quantity: this.state.qty,
        properties: this.cleanProps({ _build_id: buildId, Delivery: this.config.delivery })
      }];

      this.config.addons.forEach((addon) => {
        if (this.state.addons[addon.key] && addon.available) {
          items.push({
            id: addon.variantId,
            quantity: 1,
            properties: this.cleanProps({ _build_id: buildId, 'Added with': this.config.productTitle })
          });
        }
      });
      return items;
    }

    cleanProps(obj) {
      const out = {};
      Object.keys(obj).forEach((k) => { if (obj[k]) out[k] = obj[k]; });
      return out;
    }

    async addToCart() {
      const variant = this.variant;
      if (!variant || !variant.available || this.atc.classList.contains('is-loading')) return;
      this.setError(null);
      this.atc.classList.add('is-loading');
      this.atc.disabled = true;

      const items = this.buildItems();

      try {
        const res = await fetch(`${window.theme.routes.cartAdd}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({
            items,
            sections: 'cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');

        this.track('upsell_add_to_cart', {
          value: this.total / 100,
          items: items.map((i) => ({ id: i.id, quantity: i.quantity }))
        });
        this.fireGa4AddToCart(items);

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        if (window.theme.settings.afterAtc === 'page') {
          window.location.href = window.theme.routes.cart;
          return;
        }

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        if (this.cartDrawer) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            detail: { opener: this.atc }
          }));
        }

        this.atc.classList.add('is-success');
      } catch (err) {
        this.setError(err.message);
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.disabled = false;
        setTimeout(() => this.atc.classList.remove('is-success'), 1400);
      }
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !msg;
      if (msg) this.errorEl.textContent = msg;
    }

    /* ---------- Analytics ---------- */
    track(event, payload) {
      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({ event, ...payload });
      }
      this.dispatchEvent(new CustomEvent(`pu:${event}`, { bubbles: true, detail: payload }));
    }

    fireGa4AddToCart(items) {
      if (typeof window.gtag !== 'function') return;
      const names = [this.config.productTitle].concat(
        this.config.addons.filter((a) => this.state.addons[a.key]).map((a) => a.title)
      );
      window.gtag('event', 'add_to_cart', {
        currency: this.config.currency || 'GBP',
        value: this.total / 100,
        items: items.map((i, idx) => ({
          item_id: i.id,
          item_name: names[idx],
          quantity: i.quantity
        }))
      });
    }

    /* ---------- Money ---------- */
    money(cents) {
      const value = (cents / 100).toFixed(2);
      const [pounds, pence] = value.split('.');
      const withCommas = pounds.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${pence}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${pence}`);
    }
  }

  customElements.define('product-upsell', ProductUpsell);
}
